import { FC, useEffect, useMemo, useRef, useState } from "react";
import clsx from "classnames";
import { useStyles } from "./FooterControl.style";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { getGameActionIcon } from "../../../models/gamepad";
import {
  useActiveElement,
  useGamepadContext,
} from "../../../contexts/GamepadProvider";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import { PageRoutes } from "../../../constants/routes";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "../../hooks";
import { selectIsDesktopView } from "../../../store/slices/appState";
import { useBackNavigation } from "../../../hooks/useBackNavigation";
import { isTV } from "../../../utils/platform";

type IFooterControlPopup = {
  onClickBack?: () => void;
  onClickMenu?: () => void;
};

export const FooterControl: FC<IFooterControlPopup> = (props) => {
  const classes = useStyles();
  const { onClickBack, onClickMenu } = props;
  const { gamepads } = useGamepadContext();
  const location = useLocation();
  const { loadingStatus, isMenuOpen } = useAppSelector(
    (state) => state.gamePlay,
  );
  const isDesktop = useAppSelector(selectIsDesktopView);
  const { inputMode } = useAppSelector((state) => state.appState);
  const { currentSnackbarMessage, isSnackbarVisible } = useAppSelector(
    (state) => state.gamePlay,
  );
  const goBack = useBackNavigation();
  const gamepadIcon = getGameActionIcon(inputMode, gamepads);

  const isGamePlay = location.pathname.startsWith(PageRoutes.GamePlay);
  const isShowMenu = isGamePlay && loadingStatus === "Done";
  const isShowVideoButton =
    isSnackbarVisible &&
    currentSnackbarMessage?.videoURL &&
    gamepadIcon.buttonY;
  const isShowSelect = !isGamePlay || loadingStatus === "Done";

  const [newGPConnected, setNewGPConnected] = useState(false);
  const firstRenderRef = useRef(!!gamepads?.length);
  const gamepadsConnected = gamepads?.filter((gp) => gp.type !== "generic");

  const isInGameLoadingScreen =
    isGamePlay && !!loadingStatus && loadingStatus !== "Done";

  const activeElement = useActiveElement();
  const isActiveConsole =
    activeElement && activeElement?.getAttribute("data-gamepad") === "menu-/";

  const shouldShowBackButton = !isGamePlay
    ? !isActiveConsole
    : isInGameLoadingScreen || isMenuOpen;

  useEffect(() => {
    if (firstRenderRef.current) {
      // ignore the initial population of gamepads
      firstRenderRef.current = false;
      return;
    }

    if (!gamepads?.length) return;
    setNewGPConnected(true);
    const timeout = setTimeout(() => setNewGPConnected(false), 5000);
    return () => clearTimeout(timeout);
  }, [gamepads?.length]);

  const onClick = () => {
    if (onClickBack) {
      onClickBack();
    } else {
      goBack();
    }
  };

  const hideFooter = useMemo(() => {
    const hasKeyboard = !("ontouchstart" in window) && isDesktop;
    if (hasKeyboard || isTV) return false;
    if (isDesktop && gamepadsConnected?.length) return false;
    return !isGamePlay || inputMode !== "gamepad";
  }, [gamepadsConnected?.length, inputMode, isDesktop, isGamePlay]);

  return (
    <div
      id="footer-control"
      className={clsx(classes.root, { hide: hideFooter })}
    >
      <div className={classes.itemContent}>
        {!gamepadsConnected?.length || inputMode !== "gamepad" ? (
          <StaticImage
            src={gamepadIcon.control.icon1x}
            src2x={gamepadIcon.control.icon2x}
            src3x={gamepadIcon.control.icon3x}
            className={classes.icon}
          />
        ) : (
          gamepadsConnected.map((gamepad) => (
            <StaticImage
              src={gamepadIcon.control.icon1x}
              src2x={gamepadIcon.control.icon2x}
              src3x={gamepadIcon.control.icon3x}
              className={classes.icon}
              key={gamepad.id}
            />
          ))
        )}
        <Typography
          variant={TypographyVariant.BodyB6Bold}
          className={clsx(classes.item, {
            [classes.showMessage]: newGPConnected,
          })}
        >
          Controller {gamepadsConnected?.length} connected
        </Typography>
      </div>
      <div className={classes.itemContent}>
        {isShowMenu && gamepadIcon.menu && (
          <div
            className={classes.buttonItem}
            onClick={onClickMenu}
            id="footer-button"
          >
            {gamepadIcon?.menuShift ? (
              <Typography
                variant={TypographyVariant.BodyB6Medium}
                className={classes.holdLabel}
              >
                <StaticImage
                  src={gamepadIcon.menuShift.icon1x}
                  src2x={gamepadIcon.menuShift.icon2x}
                  src3x={gamepadIcon.menuShift.icon3x}
                  className={classes.icon}
                />
                +
                <StaticImage
                  src={gamepadIcon.menu.icon1x}
                  src2x={gamepadIcon.menu.icon2x}
                  src3x={gamepadIcon.menu.icon3x}
                  className={classes.icon}
                />
              </Typography>
            ) : inputMode === "remote" ? (
              <StaticImage
                src={gamepadIcon.menu.icon1x}
                src2x={gamepadIcon.menu.icon2x}
                src3x={gamepadIcon.menu.icon3x}
                className={classes.icon}
              />
            ) : (
              <Typography
                variant={TypographyVariant.BodyB6Medium}
                className={classes.holdLabel}
              >
                Hold
                <StaticImage
                  src={gamepadIcon.menu.icon1x}
                  src2x={gamepadIcon.menu.icon2x}
                  src3x={gamepadIcon.menu.icon3x}
                  className={classes.icon}
                />
              </Typography>
            )}
            <Typography variant={TypographyVariant.BodyB6Medium}>
              Menu
            </Typography>
          </div>
        )}
        {isShowVideoButton && (
          <Typography
            variant={TypographyVariant.BodyB6Medium}
            className={classes.buttonItem}
          >
            {gamepadIcon?.menuShift ? (
              <Typography
                variant={TypographyVariant.BodyB6Medium}
                className={classes.holdLabel}
              >
                <StaticImage
                  src={gamepadIcon.menuShift.icon1x}
                  src2x={gamepadIcon.menuShift.icon2x}
                  src3x={gamepadIcon.menuShift.icon3x}
                  className={classes.icon}
                />
                +
                <StaticImage
                  src={gamepadIcon.buttonY.icon1x}
                  src2x={gamepadIcon.buttonY.icon2x}
                  src3x={gamepadIcon.buttonY.icon3x}
                  className={classes.icon}
                />
              </Typography>
            ) : (
              <StaticImage
                src={gamepadIcon.buttonY.icon1x}
                src2x={gamepadIcon.buttonY.icon2x}
                src3x={gamepadIcon.buttonY.icon3x}
                className={classes.icon}
              />
            )}
            Video
          </Typography>
        )}
        {shouldShowBackButton && (
          <Typography
            variant={TypographyVariant.BodyB6Medium}
            className={classes.buttonItem}
            onClick={onClick}
            id="footer-quit-button"
          >
            <StaticImage
              src={gamepadIcon.buttonB.icon1x}
              src2x={gamepadIcon.buttonB.icon2x}
              src3x={gamepadIcon.buttonB.icon3x}
              className={classes.icon}
            />
            Back
          </Typography>
        )}
        {isShowSelect && (
          <Typography
            variant={TypographyVariant.BodyB6Medium}
            className={classes.buttonItem}
          >
            <StaticImage
              src={gamepadIcon.buttonA.icon1x}
              src2x={gamepadIcon.buttonA.icon2x}
              src3x={gamepadIcon.buttonA.icon3x}
              className={classes.icon}
            />
            Select
          </Typography>
        )}
      </div>
    </div>
  );
};
